// Generated by tutorials/record.cjs from tutorials/articles/h27-tray-tickets.cjs.
// Do not edit: change the script and run `node tutorials/record.cjs h27 --text-only` (wording) or
// `node tutorials/record.cjs h27` (pictures and clips).
import type { HelpArticle } from '../types';

const article: HelpArticle = {
  slug: 'tray-tickets',
  catalogId: 'h27',
  title: 'Previewing and printing tray tickets',
  summary: 'Check a resident\'s tray ticket on screen before it prints, print a whole meal\'s tickets in tray line order, and reprint one resident\'s ticket without reprinting the meal.',
  audience: 'Dietary managers and kitchen staff who print tray tickets',
  area: 'kitchen',
  learn: [
    'Preview any resident\'s ticket for any meal in the Tray Ticket Visualizer',
    'Read a tray ticket',
    'Reprint one resident\'s ticket',
    'Print a meal\'s tickets from Reports',
    'Find printed tickets in the Print Queue',
  ],
  keywords: [
    'tray ticket',
    'tray tickets',
    'ticket',
    'print',
    'reprint',
    'preview',
    'visualizer',
    'tray card',
    'meal ticket',
    'print queue',
    'tray line order',
  ],
  steps: [
    {
      id: 'open',
      part: 'Preview a ticket',
      heading: 'Open the Tray Ticket Visualizer',
      text: 'On the Dashboard, select Tray Ticket Visualizer. You can also open it from a resident\'s page, or from Visualizer on the Residents page.',
      media: {
        image: {
          src: '/help/tray-tickets/open.webp',
          width: 1280,
          height: 842,
        },
        clip: {
          src: '/help/tray-tickets/open.mp4',
          width: 1280,
          height: 842,
          durationSec: 6,
        },
        alt: 'The Tray Ticket Visualizer opening over the dashboard, showing a resident\'s breakfast ticket.',
      },
    },
    {
      id: 'pick',
      part: 'Preview a ticket',
      heading: 'Pick the resident, date and meal',
      text: 'Type the resident\'s name in the list at the top and pick them. Use the arrows beside the date to go a day forward or back, and pick the meal on the right.',
      media: {
        image: {
          src: '/help/tray-tickets/pick.webp',
          width: 1280,
          height: 890,
        },
        clip: {
          src: '/help/tray-tickets/pick.mp4',
          width: 1280,
          height: 890,
          durationSec: 6,
        },
        alt: 'The Visualizer with Tanaka, Yuki picked, today\'s date and Lunch selected.',
      },
    },
    {
      id: 'read',
      part: 'Preview a ticket',
      heading: 'Read the ticket',
      text: 'The top shows the diet order, with the consistencies marked, and the allergies in red. The grey band is the meal with the resident\'s set and tray on the tray line.\n\nEach line is one item: its consistency, the portion, the item, hot or cold, and what it is served on. Notices on the right lists the alerts for this meal.',
      media: {
        image: {
          src: '/help/tray-tickets/read.webp',
          width: 1280,
          height: 890,
        },
        alt: 'Yuki Tanaka\'s lunch ticket: Regular, Regular and Thin Liquids at the top, ALLERGY: Peanuts, Tree Nuts in red, Lunch Set 1 Tray 10, the lunch items, and the allergy alerts under Notices.',
      },
    },
    {
      id: 'print-one',
      part: 'Reprint one resident\'s ticket',
      heading: 'Print this resident\'s ticket',
      text: 'Select Print. Confirm the date and check the meals to print. Only this resident\'s ticket is printed, so the rest of the meal is not printed again.',
      media: {
        image: {
          src: '/help/tray-tickets/print-one.webp',
          width: 708,
          height: 718,
        },
        clip: {
          src: '/help/tray-tickets/print-one.mp4',
          width: 708,
          height: 718,
          durationSec: 3.1,
        },
        alt: 'The Print Report window over the Visualizer, with today\'s date, 1 day and Lunch checked.',
      },
    },
    {
      id: 'printing',
      part: 'Reprint one resident\'s ticket',
      heading: 'Print',
      text: 'Select Print. Dietech makes the ticket and opens it in your browser\'s print window when it is ready, so you can pick the printer.',
      media: {
        image: {
          src: '/help/tray-tickets/printing.webp',
          width: 1280,
          height: 842,
        },
        clip: {
          src: '/help/tray-tickets/printing.mp4',
          width: 1280,
          height: 842,
          durationSec: 5.1,
        },
        alt: 'A message at the top right: Generating the Tray Ticket Visualizer report for Tanaka, Yuki, Lunch; the viewer will open when ready.',
      },
    },
    {
      id: 'report',
      part: 'Print a meal',
      heading: 'Open the Tray Tickets report',
      text: 'To print tickets for a whole meal, select Reports in the menu, type tray tickets in Search reports, and select Tray Tickets.',
      media: {
        image: {
          src: '/help/tray-tickets/report.webp',
          width: 1280,
          height: 842,
        },
        clip: {
          src: '/help/tray-tickets/report.mp4',
          width: 1280,
          height: 842,
          durationSec: 4.9,
        },
        alt: 'The Reports page searched for tray tickets, with the Tray Tickets card.',
      },
    },
    {
      id: 'options',
      part: 'Print a meal',
      heading: 'Pick the meal and the order',
      text: 'Select Tray Tickets. Confirm the date, and check the meal under Mealtimes. Order By Tray Line prints them in the order your tray line runs.\n\nUnder Print, All prints every resident. Pick Set, Unit or Resident to print only some.',
      media: {
        image: {
          src: '/help/tray-tickets/options.webp',
          width: 1280,
          height: 842,
        },
        clip: {
          src: '/help/tray-tickets/options.mp4',
          width: 1280,
          height: 842,
          durationSec: 4.8,
        },
        alt: 'The Tray Tickets report: today\'s date, Lunch checked under Mealtimes, Order By Tray Line and Print All.',
      },
    },
    {
      id: 'generate',
      part: 'Print a meal',
      heading: 'Generate',
      text: 'Select Generate. Dietech makes the tickets and says when they are queued.',
      media: {
        image: {
          src: '/help/tray-tickets/generate.webp',
          width: 1280,
          height: 842,
        },
        clip: {
          src: '/help/tray-tickets/generate.mp4',
          width: 1280,
          height: 842,
          durationSec: 3.6,
        },
        alt: 'The Tray Tickets report with a message that the report was queued.',
      },
    },
    {
      id: 'queue',
      part: 'Print a meal',
      heading: 'Print from the Print Queue',
      text: 'Select Print Queue under Reports. Your tickets are listed with when they were made. Open them to print, or to print them again later.',
      media: {
        image: {
          src: '/help/tray-tickets/queue.webp',
          width: 1280,
          height: 842,
        },
        clip: {
          src: '/help/tray-tickets/queue.mp4',
          width: 1280,
          height: 842,
          durationSec: 4.1,
        },
        alt: 'The Print Queue listing the Tray Tickets report and the Tray Ticket Visualizer ticket for Tanaka, Yuki.',
      },
    },
  ],
  tips: [
    'Preview a ticket right after you change a diet order, an allergy or a meal pattern, to check the kitchen will see what you meant.',
    'Order By Tray Line prints the tickets in the order your tray line runs, cart by cart. Set it up on Tray Line Setup.',
    'To print tickets for a few residents only, pick Resident under Print in the Tray Tickets report.',
  ],
  notes: [
    'A ticket prints the diet order and allergies as they are when it is made. If you change an order after printing, reprint that resident\'s ticket.',
    'Tickets you print are listed in the Print Queue, where you can open them again.',
  ],
  related: ['tray-line-setup', 'change-diet-order', 'allergies-likes-dislikes'],
  recorded: '2026-10-25',
};

export default article;
